"use client";

/**
 * ConsentGatedAnalytics — mounts the analytics providers only after the
 * user has explicitly granted analytics consent.
 *
 * Providers gated here (all classified non-essential):
 *   - Vercel Web Analytics  (@vercel/analytics/next)
 *   - Vercel Speed Insights (@vercel/speed-insights/next)
 *   - Google Analytics 4    (manual injection — see GA4 note below,
 *                            only when NEXT_PUBLIC_GA_ID is set)
 *
 * The wrapper subscribes to the sc-consent-change window event so that
 * granting consent at runtime causes the scripts to mount without a
 * page reload. Withdrawing causes them to unmount (React will not
 * re-inject the <script> tags on the next render, and Consent Mode v2
 * has already been updated to 'denied' by setAnalyticsConsent so any
 * still-loaded gtag calls no longer set analytics cookies).
 *
 * GA4 injection note (F2 fix, 2026-09-11)
 * ---------------------------------------
 * The @next/third-parties GoogleAnalytics component renders two
 * <Script> tags — an external gtag/js loader and an inline
 * `dangerouslySetInnerHTML` config script. Both go through
 * next/script's `loadScript` path, which creates a <script> element
 * via document.createElement and appends it to document.body. That
 * path works for scripts that mount during the first render, but on
 * production we observed that when <GoogleAnalytics> mounts AFTER
 * hydration (which is exactly our case — consent is granted at
 * runtime, so ConsentGatedAnalytics returns null on first render
 * and mounts GoogleAnalytics only on the consent-change event) the
 * inline config script never reaches window.dataLayer. Result:
 * gtag/js loads but no config call fires, no /g/collect beacon is
 * sent, and no _ga cookie is set. Reproduced against production on
 * 2026-09-11 across three navigations after Accept.
 *
 * Root cause: next/script memoises loaded scripts by id via its
 * `LoadCache` set, and its post-hydration inline-script path is
 * unreliable. Rather than fight next/script for a case it is not
 * designed for, we do the smallest thing that works: inject the
 * external gtag/js script directly with document.createElement (the
 * one path the browser is guaranteed to execute for post-hydration
 * mounts) and then call gtag('js', ...) and gtag('config', gaId)
 * ourselves — no inline <script> tag at all. This produces the
 * exact same dataLayer sequence as the @next/third-parties helper,
 * and works whether the component mounts on first paint or later.
 *
 * SSR safety: renders null on the server; consent state is only
 * known after hydration.
 */

import { useEffect, useState } from "react";
import { Analytics } from "@vercel/analytics/next";
import { SpeedInsights } from "@vercel/speed-insights/next";
import {
  isAnalyticsGranted,
  CONSENT_CHANGE_EVENT,
} from "@/lib/consent";
import { readEnvValue, envValidators } from "@/lib/env";

const GA_SCRIPT_ID = "sc-ga4-loader";

/**
 * Load gtag/js and issue the js + config calls exactly once per
 * page lifetime. Safe to call from multiple mounts: the script tag
 * carries a stable id so a second call is a no-op.
 */
function initGA4(gaId: string): void {
  if (typeof window === "undefined") return;
  if (document.getElementById(GA_SCRIPT_ID)) return;

  // Ensure dataLayer + gtag stub exist. The disclosure banner already
  // seeds these with the Consent Mode v2 defaults; guard defensively
  // in case the ordering ever changes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- gtag is intentionally installed on window as a global
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer.push(args);
    };
  }

  // Fire the standard init sequence BEFORE the loader script arrives.
  // The stub above buffers into dataLayer; the real gtag.js replaces
  // window.gtag once it loads and drains the buffer.
  w.gtag("js", new Date());
  w.gtag("config", gaId);

  const s = document.createElement("script");
  s.id = GA_SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  document.head.appendChild(s);
}

export function ConsentGatedAnalytics() {
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reading client-only consent state after hydration
    setGranted(isAnalyticsGranted());
    const onChange = () => setGranted(isAnalyticsGranted());
    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!granted) return;
    // readEnv trims trailing whitespace / newlines and rejects
    // literal-escape corruption (see src/lib/env.ts). If the id is
    // present but malformed, surface a clear console error naming
    // the variable instead of loading gtag with a broken id — GA4
    // accepts trailing %0A silently and corrupts every event's
    // property id downstream.
    // Literal `process.env.NEXT_PUBLIC_GA_ID` so Next inlines the
    // build-time value into the client bundle. readEnvValue then trims,
    // rejects interior whitespace / literal escapes, and validates the
    // shape — same guarantees as server-side readEnv.
    const r = readEnvValue("NEXT_PUBLIC_GA_ID", process.env.NEXT_PUBLIC_GA_ID, {
      validate: envValidators.gaMeasurementId,
    });
    if (!r.ok) {
      if (r.reason !== "missing") console.error(`[analytics] ${r.message}`);
      return;
    }
    initGA4(r.value);
  }, [granted]);

  if (!granted) return null;

  return (
    <>
      <Analytics />
      <SpeedInsights />
    </>
  );
}
